/**
 * Modal "Cancelar ausencia" (008 · T076 · US4; contracts/ui.md §1.5): motivo obligatorio (1–300,
 * `CancelAbsenceBodySchema`). Una ausencia nunca se borra: queda en el historial como cancelada y
 * sus días vuelven a evaluarse normalmente. `409 PERIOD_CLOSED` / `INTEGRATION_MANAGED` se explican
 * bajo el motivo.
 */
import { useState } from 'react';

import { CancelAbsenceBodySchema, type Absence } from '@fermin/contracts';

import { Button } from '../../components/ui/button';
import { Dialog, DialogContent } from '../../components/ui/dialog';
import { Field } from '../../components/ui/field';
import { Textarea } from '../../components/ui/input';
import { useToast } from '../../components/ui/toast';
import { t } from '../../i18n';
import { scheduleErrorMessage } from '../schedules/api';
import { readOnlyProps, useReadOnly } from '../shell/use-read-only';

import { absenceTypeLabel } from './absence-type-badge';
import { useCancelAbsence } from './api';
import { absenceRangeLabel } from './format';

export const CANCEL_REASON_MAX = 300;

export interface CancelAbsenceDialogProps {
  absence: Absence | null;
  onClose: () => void;
}

/** El contenido se monta por ausencia: cada apertura empieza con el motivo vacío. */
export function CancelAbsenceDialog({ absence, onClose }: CancelAbsenceDialogProps) {
  return (
    <Dialog open={absence !== null} onOpenChange={(open) => (open ? undefined : onClose())}>
      {absence ? (
        <CancelAbsenceContent key={absence.id} absence={absence} onClose={onClose} />
      ) : null}
    </Dialog>
  );
}

function CancelAbsenceContent({ absence, onClose }: { absence: Absence; onClose: () => void }) {
  const { toast } = useToast();
  const { readOnly } = useReadOnly();
  const cancel = useCancelAbsence();
  const [reason, setReason] = useState('');
  const [error, setError] = useState<string | undefined>(undefined);

  function submit() {
    const parsed = CancelAbsenceBodySchema.safeParse({ reason });
    if (!parsed.success) {
      setError(t('absences.cancel.reasonRequired'));
      return;
    }
    setError(undefined);
    cancel.mutate(
      { id: absence.id, body: parsed.data },
      {
        onSuccess: (saved) => {
          toast({
            title: t('absences.toast.cancelled', {
              type: absenceTypeLabel(saved.type, saved.partial),
              worker: saved.worker.name,
            }),
            variant: 'success',
          });
          onClose();
        },
        onError: (apiError) => setError(scheduleErrorMessage(apiError)),
      },
    );
  }

  return (
    <DialogContent
      title={t('absences.cancel.title')}
      description={t('absences.cancel.description', {
        type: absenceTypeLabel(absence.type, absence.partial),
        worker: absence.worker.name,
        range: absenceRangeLabel(absence),
      })}
      footer={
        <>
          <Button variant="ghost" onClick={onClose} disabled={cancel.isPending}>
            {t('common.buttons.back')}
          </Button>
          <Button
            variant="destructive"
            type="submit"
            form="absence-cancel-form"
            loading={cancel.isPending}
            loadingText={t('common.states.saving')}
            {...readOnlyProps(readOnly)}
          >
            {t('absences.cancel.confirm')}
          </Button>
        </>
      }
    >
      <form
        id="absence-cancel-form"
        data-testid="absence-cancel"
        className="flex flex-col gap-fm-3"
        noValidate
        onSubmit={(event) => {
          event.preventDefault();
          submit();
        }}
      >
        <p className="text-fm-sm text-fm-text-secondary">{t('absences.cancel.notice')}</p>
        <Field
          label={t('absences.cancel.reason')}
          required
          help={t('absences.form.noteCount', { count: reason.length, max: CANCEL_REASON_MAX })}
          error={error}
        >
          {(control) => (
            <Textarea
              {...control}
              maxLength={CANCEL_REASON_MAX}
              value={reason}
              onChange={(event) => setReason(event.target.value)}
            />
          )}
        </Field>
      </form>
    </DialogContent>
  );
}
